/**
 * Homeowner Dashboard API Service
 * Maps existing backend API responses to dashboard component prop shapes
 */

import projectPlaceholder from "@/assets/images/project-placeholder.svg";
import type { CommentDto } from "@/features/comments/types";
import type {
	ActivityLog,
	DesignerProfile,
	Milestone,
	Project,
	ProjectContractorItem,
} from "@/features/projects/services/projects-api.service";
import i18n from "@/i18n";
import { apiClient } from "@/lib/api-client";
import { fetchDesignerReviews } from "@/services/reviews-api.service";
import type { AvatarStyle } from "../components/dashboard-primitives";
import {
	capitalize,
	formatCountForUI,
	formatCurrency,
	getAvatarStyleFromName,
	getInitials,
} from "../utils/string-helpers";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES - Dashboard Component Props
// ============================================================================

export interface DashboardProjectHero {
	id: string;
	name: string;
	location: string;
	propertyType: string;
	area: string;
	startDate: string;
	startYear: string;
	handoverDate: string;
	handoverYear: string;
	/** Title of the milestone in progress; null before any milestone exists. */
	stage: string | null;
	/** Share of milestones completed; null before any milestone exists. */
	progress: number | null;
	daysIn: number;
	/** Null when the project has no timeline set. */
	totalDays: number | null;
	imageUrl: string;
	nextMilestone: string | null;
	milestoneAmount: string | null;
	/** 1-based position of `nextMilestone`; null when none is pending. */
	stageNumber: number | null;
	totalStages: number;
	/** One entry per milestone in order: true when it is completed. */
	segments: boolean[];
	recentComment?: {
		content: string;
		authorName: string;
		timeAgo: string;
	} | null;
}

export interface DashboardBudget {
	released: number;
	committed: number;
	awaiting: number;
	remaining: number;
	percentage: number;
}

export interface DashboardPhase {
	name: string;
	progress: number;
	status: string;
}

export interface DashboardTeamMember {
	initials: string;
	role: string;
	name: string;
	style: string;
	online: boolean;
	avatarStyle: AvatarStyle;
	userId?: string;
}

export interface DashboardActivity {
	initials: string;
	avatarStyle: AvatarStyle;
	title: string;
	subtitle: string;
	time: string;
}

export interface DashboardProposal {
	proposalId: string;
	projectId: string;
	title: string;
	designerInitials: string;
	designerAvatarStyle: AvatarStyle;
	designerName: string;
	designerStyle: string | null;
	designerRating: number | null;
	designerImageUrl: string | null;
	timeAgo: string;
	quoteText: string;
	amount: string;
	costEstimate: number;
	/** Other submitted proposals on this project that the card does not show. */
	moreCount: number;
}

export interface DashboardDeskDesigner {
	name: string;
	initials: string;
	location: string | null;
	style: string | null;
	rating: number | null;
	bio: string | null;
	avatarStyle: AvatarStyle;
	imageUrl: string | null;
	isVerified: boolean;
}

export interface DashboardDeskThreads {
	proposal: DashboardProposal | null;
	designer: DashboardDeskDesigner | null;
}

export interface DeskDesignerRatings {
	proposer: number | null;
	assigned: number | null;
}

export interface HeroThreadPart {
	type: "normal" | "italic";
	text: string;
}

export interface BeforeAfterPair {
	beforeUrl: string;
	afterUrl: string;
}

export interface HomeownerDashboardData {
	userName: string;
	project: DashboardProjectHero | null;
	budget: DashboardBudget | null;
	phases: DashboardPhase[];
	team: DashboardTeamMember[];
	activities: DashboardActivity[];
	deskThreads: DashboardDeskThreads | null;
	threadParts: HeroThreadPart[];
	beforeAfterImages: BeforeAfterPair[];
	sectionErrors: Record<string, string>;
}

// ============================================================================
// API RESPONSE TYPES (from existing endpoints)
// ============================================================================

interface HomeownerProfileResponse {
	data: {
		profile: {
			id: string;
			name: string;
			email?: string;
			city?: string;
			locality?: string;
			profilePictureUrl?: string;
		};
	};
}

interface ProjectPaymentsResponse {
	data: {
		transactions: Array<{
			id: string;
			amount: number;
			status: string;
			providerOrderId?: string | null;
			releasedAmount?: number;
			refundedAmount?: number;
			milestoneTitle?: string;
			metadata?: Record<string, unknown> | null;
		}>;
		summary: {
			totalFunded: number;
			totalHeld: number;
			totalReleased: number;
		};
	};
}

interface ProjectProposalsResponse {
	data: {
		proposals: Array<{
			proposal: {
				id: string;
				scopeDescription?: string;
				costEstimate: number;
				status: string;
				createdAt: string;
			};
			designer: {
				id: string;
				name: string;
				styles?: string[];
				profilePictureUrl?: string | null;
			};
		}>;
	};
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function isValidDateString(value: string | null | undefined): value is string {
	if (!value) return false;
	const date = new Date(value);
	return !Number.isNaN(date.getTime());
}

function formatDate(dateStr: string): { date: string; year: string } {
	const date = new Date(dateStr);
	const day = date.getDate();
	const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
	const year = date.getFullYear().toString();
	return { date: `${day} ${month}`, year };
}

/** The `startTimeline` values the project form can store, and their labels. */
const START_TIMELINE_LABEL_KEYS: Record<string, string> = {
	immediately: "createProject.immediately",
	within_month: "createProject.withinMonth",
	"1_3_months": "createProject.oneToThreeMonths",
	"3_6_months": "createProject.threeToSixMonths",
	flexible: "createProject.flexible",
};

function formatTimelineString(timeline: string | null | undefined): {
	date: string;
	year: string;
} {
	if (!timeline) return { date: "TBD", year: "" };
	if (isValidDateString(timeline)) {
		return formatDate(timeline);
	}
	const value: string = timeline;
	const labelKey = START_TIMELINE_LABEL_KEYS[value];
	if (labelKey) return { date: i18n.t(labelKey), year: "" };
	return { date: value.replace(/_/g, " ").trim(), year: "" };
}

function computeDaysIn(startTimeline: string | null | undefined): number {
	if (!isValidDateString(startTimeline)) return 0;
	// biome-ignore lint/style/noNonNullAssertion: guarded by isValidDateString check above
	const start = new Date(startTimeline!);
	const now = new Date();
	const diff = Math.floor(
		(now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
	);
	return Math.max(0, diff);
}

function mapScopeToPropertyType(scope: string | null | undefined): string {
	if (!scope) return "Residential";
	switch (scope) {
		case "full_home":
			return "Full";
		case "partial":
			return "Partial";
		default:
			return scope;
	}
}

function mapMilestoneStatusToProgress(milestone: Milestone): {
	progress: number;
	status: string;
} {
	const amount = milestone.paymentAmount
		? formatCurrency(milestone.paymentAmount)
		: null;
	if (milestone.status === "completed") {
		return {
			progress: 100,
			status:
				amount && milestone.fundingStatus === "released"
					? `Complete · ${amount} released`
					: "Complete",
		};
	}
	if (!amount) return { progress: 0, status: "Pending" };
	return {
		progress: 0,
		status:
			milestone.fundingStatus === "funded"
				? `Pending · ${amount} funded`
				: `Pending · ${amount}`,
	};
}

function formatActionTitle(action: string): string {
	if (!action) return "Unknown Action";
	return action
		.toUpperCase()
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

function enrichActivity(activity: ActivityLog): DashboardActivity {
	const title = formatActionTitle(activity.action);
	const initials = getInitials(title);
	let subtitle = "";

	if (typeof activity.details === "string") {
		subtitle = activity.details;
	} else if (activity.details && typeof activity.details === "object") {
		const details = activity.details;
		if ("milestone" in details && details.milestone)
			subtitle = String(details.milestone);
		else if ("message" in details && details.message)
			subtitle = String(details.message);
		else if ("description" in details && details.description)
			subtitle = String(details.description);
	}

	const time = formatActivityTime(activity.createdAt);

	const avatarStyle: AvatarStyle = getAvatarStyleFromName(title);

	return { initials, avatarStyle, title, subtitle, time };
}

export function formatActivityTime(dateStr: string): string {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffDays === 0) {
		const time = date.toLocaleTimeString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		});
		return `${time} Today`;
	}
	if (diffDays === 1) return "Yesterday";
	if (diffDays > 1 && diffDays < 7) {
		const targetDate = new Date(now.getTime() - diffDays * 24 * 60 * 60 * 1000);
		const targetDay = targetDate.getDate();
		const month = targetDate.toLocaleDateString("en-US", { month: "short" });
		return `${targetDay} ${month}`;
	}
	if (diffDays < 0) {
		const absDays = Math.abs(diffDays);
		const targetDate = new Date(now.getTime() + absDays * 24 * 60 * 60 * 1000);
		const targetDay = targetDate.getDate();
		const month = targetDate.toLocaleDateString("en-US", { month: "short" });
		return `${targetDay} ${month} Today`;
	}

	const targetDate = new Date(now.getTime() - diffDays * 24 * 60 * 60 * 1000);
	const targetDay = targetDate.getDate();
	const month = targetDate.toLocaleDateString("en-US", { month: "short" });
	return `${targetDay} ${month}`;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export async function getHomeownerName(): Promise<string> {
	try {
		const response = await apiClient.get<HomeownerProfileResponse>(
			`${API_BASE}/homeowners/me`,
		);
		return response.data.data.profile?.name ?? "";
	} catch {
		return "";
	}
}

export async function getActiveProject(): Promise<{
	project: Project | null;
	property: { city?: string; locality?: string; sizeSqft?: number } | null;
}> {
	try {
		const response = await apiClient.get<{ data: { projects: Project[] } }>(
			`${API_BASE}/projects`,
		);
		const projects = response.data.data?.projects ?? [];
		const active = projects.find(
			(p) => p.status !== "completed" && p.status !== "cancelled",
		);
		if (!active) return { project: null, property: null };

		let property = null;
		if (active.propertyId) {
			try {
				const propResponse = await apiClient.get<{
					data: {
						property: { city?: string; locality?: string; sizeSqft?: number };
					};
				}>(`${API_BASE}/homeowners/me/properties/${active.propertyId}`);
				property = propResponse.data.data?.property ?? null;
			} catch {
				property = null;
			}
		}

		return { project: active, property };
	} catch {
		return { project: null, property: null };
	}
}

export async function getProjectMilestonesData(
	projectId: string,
): Promise<Milestone[]> {
	try {
		const response = await apiClient.get<{
			data: { milestones: Milestone[] };
		}>(`${API_BASE}/projects/${projectId}/milestones`);
		return response.data.data?.milestones ?? [];
	} catch {
		return [];
	}
}

export async function getProjectContractorsData(
	projectId: string,
): Promise<ProjectContractorItem[]> {
	try {
		const response = await apiClient.get<{
			data: { contractors: ProjectContractorItem[] };
		}>(`${API_BASE}/projects/${projectId}/contractors`);
		return response.data.data?.contractors ?? [];
	} catch {
		return [];
	}
}

export async function getProjectDesigner(
	projectId: string,
): Promise<DesignerProfile | null> {
	try {
		const response = await apiClient.get<{
			data: { project: { designerId: string } };
		}>(`${API_BASE}/projects/${projectId}`);
		const designerId = response.data.data?.project?.designerId;
		if (!designerId) return null;

		const designerResponse = await apiClient.get<{
			data: { designer: DesignerProfile };
		}>(`${API_BASE}/designers/${designerId}`);
		return designerResponse.data.data?.designer ?? null;
	} catch {
		return null;
	}
}

export async function getProjectActivityData(
	projectId: string,
): Promise<ActivityLog[]> {
	try {
		const response = await apiClient.get<{
			data: { activities: ActivityLog[] };
		}>(`${API_BASE}/projects/${projectId}/activity`);
		return response.data.data?.activities ?? [];
	} catch {
		return [];
	}
}

export async function getProjectComments(
	projectId: string,
): Promise<CommentDto[]> {
	try {
		const response = await apiClient.get<{
			data: { comments: CommentDto[] };
		}>(`${API_BASE}/comments/project/${projectId}`);
		return response.data.data?.comments ?? [];
	} catch {
		return [];
	}
}

export async function getProjectPaymentsData(
	projectId: string,
): Promise<ProjectPaymentsResponse["data"] | null> {
	try {
		const response = await apiClient.get<ProjectPaymentsResponse>(
			`${API_BASE}/projects/${projectId}/payments`,
		);
		return response.data.data ?? null;
	} catch {
		return null;
	}
}

export async function getProjectProposalsData(
	projectId: string,
): Promise<ProjectProposalsResponse["data"] | null> {
	try {
		const response = await apiClient.get<ProjectProposalsResponse>(
			`${API_BASE}/requests/project/${projectId}/proposals`,
		);
		return response.data.data ?? null;
	} catch {
		return null;
	}
}

// ============================================================================
// DATA TRANSFORMERS
// ============================================================================

export function mapProjectToHero(
	project: Project,
	property: { city?: string; locality?: string; sizeSqft?: number } | null,
	milestones: Milestone[],
	recentCommentData?: CommentDto | null,
	activityData: ActivityLog[] = [],
): DashboardProjectHero {
	const location = [property?.city, property?.locality]
		.filter(Boolean)
		.join(" · ");
	const area = property?.sizeSqft
		? `${property.sizeSqft.toLocaleString("en-IN")} sq ft`
		: "";

	let activeFromDate = project.createdAt;
	let hasStarted = false;

	if (
		(project.status as string) === "active" ||
		project.status === "in_progress" ||
		project.status === "completed"
	) {
		hasStarted = true;
		// Find the date the project transitioned to in_progress / active
		let activationDate: string | null = null;
		for (const activity of activityData) {
			if (activity.action === "status_changed") {
				const details =
					typeof activity.details === "string"
						? JSON.parse(activity.details)
						: activity.details;
				if (
					details &&
					(details.to === "in_progress" || details.to === "active")
				) {
					activationDate = activity.createdAt;
					break;
				}
			}
		}

		if (activationDate && isValidDateString(activationDate)) {
			activeFromDate = activationDate;
		} else if (
			project.startTimeline &&
			isValidDateString(project.startTimeline)
		) {
			activeFromDate = project.startTimeline;
		} else if (project.createdAt && isValidDateString(project.createdAt)) {
			activeFromDate = project.createdAt;
		} else {
			hasStarted = false;
		}
	}

	const totalDays = project.timelineWeeks ? project.timelineWeeks * 7 : null;
	const daysIn = hasStarted
		? computeDaysIn(activeFromDate)
		: computeDaysIn(project.startTimeline);

	const ordered = [...milestones].sort((a, b) => a.orderIndex - b.orderIndex);
	const nextMilestone = ordered.find((m) => m.status === "pending");
	const milestoneAmount = nextMilestone?.paymentAmount
		? formatCurrency(nextMilestone.paymentAmount)
		: null;

	const completedPhases = ordered.filter(
		(m) => m.status === "completed",
	).length;
	const stage = nextMilestone
		? nextMilestone.title
		: ordered.length > 0
			? "Complete"
			: null;

	const startFormatted = hasStarted
		? formatDate(activeFromDate)
		: formatTimelineString(project.startTimeline);

	const handoverDate =
		hasStarted && project.timelineWeeks
			? formatDate(
					new Date(
						new Date(activeFromDate).getTime() +
							project.timelineWeeks * 7 * 24 * 60 * 60 * 1000,
					).toISOString(),
				)
			: { date: "TBD", year: "" };

	const recentComment = recentCommentData
		? {
				content: recentCommentData.content,
				authorName: recentCommentData.author?.displayName || "System",
				timeAgo: formatTimeAgo(recentCommentData.createdAt),
			}
		: null;

	return {
		id: project.id,
		name: project.title,
		location,
		propertyType: mapScopeToPropertyType(project.scope),
		area,
		startDate: startFormatted.date,
		startYear: startFormatted.year,
		handoverDate: handoverDate.date,
		handoverYear: handoverDate.year,
		stage,
		progress:
			ordered.length > 0
				? Math.round((completedPhases / ordered.length) * 100)
				: null,
		daysIn,
		totalDays,
		imageUrl: project.projectImages?.[0] ?? projectPlaceholder,
		nextMilestone: nextMilestone?.title ?? null,
		milestoneAmount,
		stageNumber: nextMilestone ? ordered.indexOf(nextMilestone) + 1 : null,
		totalStages: ordered.length,
		segments: ordered.map((m) => m.status === "completed"),
		recentComment,
	};
}

export function mapMilestonesToPhases(
	milestones: Milestone[],
): DashboardPhase[] {
	return milestones.map((milestone) => {
		const { progress, status } = mapMilestoneStatusToProgress(milestone);
		return {
			name: milestone.title,
			progress,
			status,
		};
	});
}

export function mapContractorsToTeam(
	contractors: ProjectContractorItem[],
	designer: DesignerProfile | null,
): DashboardTeamMember[] {
	const members: DashboardTeamMember[] = [];

	if (designer) {
		members.push({
			initials: getInitials(designer.name),
			role: "DESIGNER · LEAD",
			name: designer.name,
			style: designer.styles?.join(" · ") ?? "Contemporary",
			online: false,
			avatarStyle: getAvatarStyleFromName(designer.name),
			userId: designer.userId,
		});
	}

	for (const contractor of contractors) {
		if (contractor.status === "hired" && contractor.contractor) {
			members.push({
				initials: getInitials(contractor.contractor.name),
				role:
					(contractor.contractor.trades?.[0] ?? "").toUpperCase() ||
					"CONTRACTOR",
				name: contractor.contractor.name,
				style: contractor.contractor.experienceYears
					? `${contractor.contractor.experienceYears} yrs`
					: "",
				online: false,
				avatarStyle: getAvatarStyleFromName(contractor.contractor.name),
				userId: contractor.contractor.userId,
			});
		}
	}

	return members;
}

export function enrichActivities(
	activities: ActivityLog[],
): DashboardActivity[] {
	const filtered = activities.filter(
		(activity) =>
			activity.action !== "milestone_status_changed" && activity.action != null,
	);

	const seen = new Set<string>();
	const deduped = filtered.filter((activity) => {
		const key = `${activity.action}-${activity.createdAt}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});

	const sorted = deduped.sort((a, b) => {
		const dateA = new Date(a.createdAt).getTime();
		const dateB = new Date(b.createdAt).getTime();
		if (Number.isNaN(dateA)) return 1;
		if (Number.isNaN(dateB)) return -1;
		return dateB - dateA;
	});

	return sorted.map((activity) => enrichActivity(activity));
}

function joinStyles(styles: string[] | undefined): string | null {
	return styles?.length ? styles.join(" · ") : null;
}

/**
 * A designer's average rating, or null when they have no reviews or the
 * request fails: the card shows no rating rather than a made-up one.
 */
export async function getDesignerRating(
	designerId: string | undefined,
): Promise<number | null> {
	if (!designerId) return null;
	try {
		const { summary } = await fetchDesignerReviews(designerId, 1);
		return summary.reviewCount > 0 ? summary.averageRating : null;
	} catch {
		return null;
	}
}

export function mapProposalsToDeskThreads(
	projectId: string,
	projectTitle: string,
	proposalsData: ProjectProposalsResponse["data"] | null,
	designerProfile: DesignerProfile | null,
	ratings: DeskDesignerRatings = { proposer: null, assigned: null },
): DashboardDeskThreads {
	const assignedDesigner: DashboardDeskDesigner | null = designerProfile
		? {
				name: designerProfile.name,
				initials: getInitials(designerProfile.name),
				location: designerProfile.serviceCities?.[0] ?? null,
				style: joinStyles(designerProfile.styles),
				rating: ratings.assigned,
				bio: designerProfile.bio || null,
				avatarStyle: "warm",
				imageUrl: designerProfile.profilePictureUrl ?? null,
				isVerified: designerProfile.isVerified,
			}
		: null;

	const submitted = (proposalsData?.proposals ?? []).filter(
		(p) => p.proposal?.status === "submitted",
	);
	const latestProposal = submitted[0];
	if (!latestProposal?.proposal) {
		return { proposal: null, designer: assignedDesigner };
	}

	const proposal = latestProposal.proposal;
	const designerMeta = latestProposal.designer;

	return {
		proposal: {
			proposalId: proposal.id,
			projectId,
			title: projectTitle,
			designerInitials: getInitials(designerMeta.name),
			designerAvatarStyle: "warm",
			designerName: designerMeta.name,
			designerStyle: joinStyles(designerMeta.styles),
			designerRating: ratings.proposer,
			designerImageUrl: designerMeta.profilePictureUrl ?? null,
			timeAgo: formatTimeAgo(proposal.createdAt),
			quoteText: proposal.scopeDescription ?? "",
			amount: formatCurrency(proposal.costEstimate),
			costEstimate: proposal.costEstimate,
			moreCount: submitted.length - 1,
		},
		designer: assignedDesigner,
	};
}

export function formatTimeAgo(dateStr: string): string {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

	if (diffHours < 1) return "JUST NOW";
	if (diffHours < 24) return `${diffHours}H AGO`;
	const diffDays = Math.floor(diffHours / 24);
	if (diffDays === 1) return "YESTERDAY";
	if (diffDays < 7) return `${diffDays}D AGO`;
	return date.toLocaleDateString("en-US", {
		day: "numeric",
		month: "short",
	});
}

/**
 * The statuses in which a funding row records a charge the provider took.
 * Equal to `FUNDED_ESCROW_STATUSES` in the backend's `payments.service.ts`.
 */
const FUNDED_STATUSES: readonly string[] = [
	"captured",
	"held",
	"released",
	"refunded",
	"partially_refunded",
];

/**
 * Whether a `pending` row's provider-stored expiry has passed, so it is no
 * longer money the homeowner can pay and must not count as owed (D10).
 *
 * A row with no `metadata.orderExpiry` (written before U1) is not treated as
 * expired here: the backend's fallback and sweep are what retire it, and
 * `getProjectPayments` then drops the `failed` row from the payload entirely.
 */
function isExpiredPending(txn: {
	status: string;
	metadata?: Record<string, unknown> | null;
}): boolean {
	if (txn.status !== "pending") return false;
	const raw = txn.metadata?.orderExpiry;
	if (typeof raw !== "string") return false;
	const expiry = Date.parse(raw);
	return !Number.isNaN(expiry) && expiry <= Date.now();
}

export function computeBudget(
	paymentsData: ProjectPaymentsResponse["data"] | null,
): DashboardBudget | null {
	if (!paymentsData?.summary) return null;

	// The same four quantities `getProjectPayments` in the backend's
	// `payments.service.ts` derives its summary from, computed from the rows
	// because REMAINING needs the `pending` ones the summary does not carry. The
	// two have to change together.
	//
	// A contractor is paid out of the money the homeowner funded. `hireContractor`
	// writes the assignment at `held` with no provider order, and
	// `authorizeAssignmentPayment` moves it to `released`. So:
	// - funded is the rows `isFundingCharge` accepts -- an order id and a status
	//   in `FUNDED_STATUSES` -- and leaves the contractor row out: it is not a
	//   second deposit.
	// - released is every `released` row, whole. A designer milestone release
	//   and a contractor payout are both the homeowner's money spent. Also the
	//   part of a `partially_refunded` funding charge not refunded (`amount`
	//   less `refundedAmount`): a split pays the designer the rest of their
	//   share and the platform keeps its fee. And the `releasedAmount` a dispute
	//   resolution's payout leg has sent from a funding charge still `held`.
	// - refunded is the funding charges at `refunded`, whole, and the
	//   `refundedAmount` of those at `partially_refunded`.
	// - awaiting is what is left: derived, not summed, so a `captured` charge is
	//   in it and a contractor's `held` quote is not added on top.
	// A `failed` row is in none of them: its one writer, `supersedeStrandedSession`
	// in the backend, closes a checkout nothing confirmed was charged.
	const transactions = paymentsData.transactions ?? [];
	const fundingCharges = transactions.filter((txn) =>
		Boolean(txn.providerOrderId),
	);
	const sumOf = (
		rows: typeof transactions,
		statuses: readonly string[],
	): number =>
		rows.reduce(
			(sum, txn) => (statuses.includes(txn.status) ? sum + txn.amount : sum),
			0,
		);

	const splits = fundingCharges.filter(
		(txn) => txn.status === "partially_refunded",
	);
	const splitCharged = splits.reduce((sum, txn) => sum + txn.amount, 0);
	const splitRefunded = splits.reduce(
		(sum, txn) => sum + (txn.refundedAmount ?? 0),
		0,
	);
	const paidFromHeld = fundingCharges
		.filter((txn) => txn.status === "held")
		.reduce((sum, txn) => sum + (txn.releasedAmount ?? 0), 0);

	const funded = sumOf(fundingCharges, FUNDED_STATUSES);
	const refunded = sumOf(fundingCharges, ["refunded"]) + splitRefunded;
	const released =
		sumOf(transactions, ["released"]) +
		(splitCharged - splitRefunded) +
		paidFromHeld;
	// Not clamped at zero. The backend refuses to hire or pay a contractor the
	// escrow cannot cover, but rows written any other way are not checked, and
	// if they say more was paid out than the homeowner put in, a negative figure
	// is the true one. The budget card shows the sign and says so.
	const awaiting = funded - released - refunded;
	// A `pending` milestone row is a payment session nobody has paid for work
	// the homeowner has agreed to, so this is money they still owe -- but only
	// while the session is live. Once its provider-stored expiry has passed
	// (D10), the session is abandoned and must not read as owed. A `pending`
	// top-up is never owed: it is money they chose to add and have not paid,
	// which no one is owed, so it is left out here -- and so out of committed --
	// until it is paid, when it is funded and joins awaiting. Told apart by
	// `metadata.type`, as the backend's `isEscrowTopUp` does.
	const remaining = sumOf(
		fundingCharges.filter(
			(txn) => txn.metadata?.type !== "top_up" && !isExpiredPending(txn),
		),
		["pending"],
	);
	// The card renders committed with AWAITING RELEASE and REMAINING beneath it,
	// so those three have to add up to it or the card contradicts itself. It
	// comes to funded - refunded + pending milestone rows, and a contractor
	// payout only moves money from awaiting to released inside it.
	// `summary.totalFunded` cannot serve as committed: it counts money charged,
	// which leaves out what is still to pay and leaves in what was refunded.
	const committed = released + awaiting + remaining;
	// Passes 100 when a contractor is paid past the funding, which is as true as
	// the negative awaiting beside it.
	const percentage =
		committed > 0 ? Math.round((released / committed) * 100) : 0;

	return {
		released,
		committed,
		awaiting,
		remaining,
		percentage,
	};
}

export function mapProjectImagesToBeforeAfter(
	projectImages: string[] | undefined,
): BeforeAfterPair[] {
	if (!projectImages || projectImages.length < 2) {
		return [];
	}
	const pairs: BeforeAfterPair[] = [];
	for (let i = 0; i + 1 < projectImages.length; i += 2) {
		pairs.push({
			beforeUrl: projectImages[i] ?? "",
			afterUrl: projectImages[i + 1] ?? "",
		});
	}
	return pairs;
}

// ============================================================================
// THREAD COMPUTATION
// ============================================================================

export function computeThreads(params: {
	project: DashboardProjectHero | null;
	proposalsData: ProjectProposalsResponse["data"] | null;
	designerProfile: DesignerProfile | null;
}): HeroThreadPart[] {
	const { project, proposalsData } = params;

	const hasProject = project !== null;
	const hasMilestone =
		hasProject &&
		typeof project.nextMilestone === "string" &&
		project.nextMilestone.length > 0;

	// The same proposals the desk card shows: only those still awaiting a
	// decision, and the designer is the one who sent the first of them, not
	// the designer already assigned to the project.
	const pendingProposal = (proposalsData?.proposals ?? []).find(
		(entry) => entry.proposal?.status === "submitted",
	);
	const hasProposal = pendingProposal !== undefined;

	const parts: HeroThreadPart[] = [];

	let threadCount = 0;

	if (hasMilestone) {
		threadCount++;
		parts.push(
			{ type: "italic", text: project?.nextMilestone ?? "" },
			{ type: "normal", text: " on " },
			{ type: "normal", text: project?.name },
		);
	}

	if (pendingProposal) {
		if (parts.length > 0) parts.push({ type: "normal", text: ", " });
		threadCount++;
		parts.push({ type: "italic", text: "proposal" });
		parts.push({ type: "normal", text: " from " });
		parts.push({ type: "normal", text: pendingProposal.designer.name });
	}

	if (threadCount === 0) {
		return [];
	}

	if (!hasProposal && parts.length > 0) {
		parts.push({ type: "normal", text: ", " });
		parts.push({ type: "normal", text: "and a " });
		parts.push({ type: "italic", text: "verified designer" });
		parts.push({ type: "normal", text: " in your neighbourhood" });
	}

	const threadWord = threadCount === 1 ? "thread" : "threads";
	const needVerb = threadCount === 1 ? "needs" : "need";
	const threadCountText = formatCountForUI(threadCount);
	const intro: HeroThreadPart[] = [
		{
			type: "normal",
			text: `${capitalize(threadCountText)} ${threadWord} ${needVerb} your attention this evening. `,
		},
	];

	return [...intro, ...parts];
}

// ============================================================================
// MAIN DASHBOARD FETCHER
// ============================================================================

export async function getHomeownerDashboard(
	signal?: AbortSignal,
): Promise<HomeownerDashboardData> {
	const sectionErrors: Record<string, string> = {};

	const userName = await getHomeownerName();
	if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

	const activeProjectData = await getActiveProject();
	if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

	let project: DashboardProjectHero | null = null;
	let phases: DashboardPhase[] = [];
	let team: DashboardTeamMember[] = [];
	let activities: DashboardActivity[] = [];
	let budget: DashboardBudget | null = null;
	let deskThreads: DashboardDeskThreads | null = null;
	let threadParts: HeroThreadPart[] = [];

	if (activeProjectData.project) {
		const [
			milestonesResult,
			contractorsResult,
			designerResult,
			activityResult,
			paymentsResult,
			proposalsResult,
			commentsResult,
		] = await Promise.allSettled([
			getProjectMilestonesData(activeProjectData.project.id),
			getProjectContractorsData(activeProjectData.project.id),
			getProjectDesigner(activeProjectData.project.id),
			getProjectActivityData(activeProjectData.project.id),
			getProjectPaymentsData(activeProjectData.project.id),
			getProjectProposalsData(activeProjectData.project.id),
			getProjectComments(activeProjectData.project.id),
		]);

		if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

		const milestones =
			milestonesResult.status === "fulfilled" ? milestonesResult.value : [];
		if (milestonesResult.status === "rejected") {
			sectionErrors.milestones = "Milestones failed to load";
		}

		const contractors =
			contractorsResult.status === "fulfilled" ? contractorsResult.value : [];
		if (contractorsResult.status === "rejected") {
			sectionErrors.contractors = "Contractors failed to load";
		}

		const designer =
			designerResult.status === "fulfilled" ? designerResult.value : null;
		if (designerResult.status === "rejected") {
			sectionErrors.designer = "Designer failed to load";
		}

		const activityData =
			activityResult.status === "fulfilled" ? activityResult.value : [];
		if (activityResult.status === "rejected") {
			sectionErrors.activity = "Activity failed to load";
		}

		const projPayments =
			paymentsResult.status === "fulfilled" ? paymentsResult.value : null;
		if (paymentsResult.status === "rejected") {
			sectionErrors.payments = "Payments failed to load";
		}

		const projProposals =
			proposalsResult.status === "fulfilled" ? proposalsResult.value : null;
		if (proposalsResult.status === "rejected") {
			sectionErrors.proposals = "Proposals failed to load";
		}

		const comments =
			commentsResult.status === "fulfilled" ? commentsResult.value : [];

		const sortedComments = [...comments].sort(
			(a, b) =>
				new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		);
		const recentComment = sortedComments[0] || null;

		project = mapProjectToHero(
			activeProjectData.project,
			activeProjectData.property,
			milestones,
			recentComment,
			activityData,
		);
		phases = mapMilestonesToPhases(milestones);
		team = mapContractorsToTeam(contractors, designer);
		activities = enrichActivities(activityData);
		budget = computeBudget(projPayments);
		const firstSubmitted = projProposals?.proposals?.find(
			(p) => p.proposal?.status === "submitted",
		);
		const [proposerRating, assignedRating] = await Promise.all([
			getDesignerRating(firstSubmitted?.designer.id),
			getDesignerRating(designer?.id),
		]);
		if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
		deskThreads = mapProposalsToDeskThreads(
			activeProjectData.project.id,
			activeProjectData.project.title,
			projProposals,
			designer,
			{ proposer: proposerRating, assigned: assignedRating },
		);

		threadParts = computeThreads({
			project,
			proposalsData: projProposals,
			designerProfile: designer,
		});
	}

	const beforeAfterImages = mapProjectImagesToBeforeAfter(
		activeProjectData.project?.projectImages,
	);

	return {
		userName,
		project,
		budget,
		phases,
		team,
		activities,
		deskThreads,
		threadParts,
		beforeAfterImages,
		sectionErrors,
	};
}
